import { useNavigate, useSearchParams } from '@remix-run/react'
import { useEffect, useState } from 'react'

export type Genre = {
	id: string
	name: string
}

/**
 * Custom hook for managing event filter state and navigation
 */
export function useEventFilters(options: {
	initialSearch?: string
	initialDate?: string
	initialGenres?: string[]
	initialCities?: string[]
}) {
	const {
		initialSearch = ``,
		initialDate,
		initialGenres = [],
		initialCities = [],
	} = options

	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	// Filter state
	const [searchInput, setSearchInput] = useState(initialSearch)
	const [dateOpen, setDateOpen] = useState(false)
	const [selectedDate, setSelectedDate] = useState<Date | undefined>(
		initialDate ? new Date(initialDate) : undefined,
	)
	const [genreOpen, setGenreOpen] = useState(false)
	const [genreSearch, setGenreSearch] = useState(``)
	const [selectedGenres, setSelectedGenres] = useState<string[]>(initialGenres)
	const [cityOpen, setCityOpen] = useState(false)
	const [citySearch, setCitySearch] = useState(``)
	const [selectedCities, setSelectedCities] = useState<string[]>(initialCities)

	// Debounced search: trigger search 300ms after user stops typing
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			performSearch({ searchTerm: searchInput })
		}, 300)

		return () => clearTimeout(timeoutId)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchInput])

	const performSearch = (options?: {
		searchTerm?: string
		date?: Date | undefined | null
		genres?: string[] | null
		cities?: string[] | null
	}) => {
		const params = new URLSearchParams(searchParams)

		const searchValue =
			options?.searchTerm !== undefined ? options.searchTerm : searchInput
		const dateValue = options && `date` in options ? options.date : selectedDate
		const genresValue =
			options && `genres` in options ? options.genres : selectedGenres
		const citiesValue =
			options && `cities` in options ? options.cities : selectedCities

		if (searchValue.trim()) {
			params.set(`search`, searchValue)
		} else {
			params.delete(`search`)
		}

		if (dateValue) {
			// Send date in YYYY-MM-DD format (timezone-neutral)
			const year = dateValue.getFullYear()
			const month = String(dateValue.getMonth() + 1).padStart(2, `0`)
			const day = String(dateValue.getDate()).padStart(2, `0`)
			params.set(`date`, `${year}-${month}-${day}`)
		} else {
			params.delete(`date`)
		}

		if (genresValue && genresValue.length > 0) {
			params.set(`genre`, genresValue.join(`,`))
		} else {
			params.delete(`genre`)
		}

		if (citiesValue && citiesValue.length > 0) {
			params.set(`city`, citiesValue.join(`,`))
		} else {
			params.delete(`city`)
		}

		// Navigate to new URL with updated params (Remix handles the loader call)
		const newUrl = params.toString() ? `/?${params.toString()}` : `/`
		navigate(newUrl, { preventScrollReset: true })
	}

	return {
		// State
		searchInput,
		setSearchInput,
		dateOpen,
		setDateOpen,
		selectedDate,
		setSelectedDate,
		genreOpen,
		setGenreOpen,
		genreSearch,
		setGenreSearch,
		selectedGenres,
		setSelectedGenres,
		cityOpen,
		setCityOpen,
		citySearch,
		setCitySearch,
		selectedCities,
		setSelectedCities,
		// Actions
		performSearch,
	}
}
